import { ChangeDetectorRef, Component, OnInit } from "@angular/core";
import { navItems } from "./sidebar-data";
import { NavigationEnd, Router } from "@angular/router";
import { MediaMatcher } from "@angular/cdk/layout";
import { AppHorizontalNavItemComponent } from "./nav-item/nav-item.component";
import { CommonModule } from "@angular/common";
import { NavService } from "../../../../../../../shared/src/lib/services/nav.service";
import { NavItem } from "../../vertical/sidebar/nav-item/nav-item";
import { ApiService } from "../../../../../../../shared/src/lib/services/api.service";
import { FeatureFlagService } from "../../../../../../../shared/src/lib/services/feature-flag.service";
import { WhiteLabelMarketConfig } from "../../../../pages/home/white-label/market-config/market-conifg.component";
import { of } from "rxjs";
import { filter, map } from "rxjs/operators";
import { MaterialModule } from "../../../../material.module";

@Component({
    selector: "app-horizontal-sidebar",
    imports: [AppHorizontalNavItemComponent, CommonModule, MaterialModule],
    templateUrl: "./sidebar.component.html",
    styleUrls: ["./sidebar.component.scss"]
})
export class AppHorizontalSidebarComponent implements OnInit {
  navItems = navItems;
  parentActive = "";
  mobileQuery: MediaQueryList;
  private readonly _mobileQueryListener: () => void;
  private isSuperAdmin: boolean = false;

  private readonly PERMISSION_NAV_MAP: { permission: string; displayNames: string[] }[] = [
    { permission: "canConfigureCasinoNavigation", displayNames: ["Casino Navigation"] },
    { permission: "canConfigureCountry", displayNames: ["Supported Countries"] },
    { permission: "canConfigureCurrency", displayNames: ["Exchange Rate"] },
    { permission: "canConfigureSport", displayNames: ["Supported Sports"] },
    { permission: "canSetupBetFairAccounts", displayNames: ["Betfair Accounts"] },
    { permission: "canViewReports", displayNames: ["Reports", "Account Statement"] },
    { permission: "canEnableWhiteLabel", displayNames: ["White Labels"] },
    { permission: "canEnableLiveStream", displayNames: ["Live Stream"] },
    { permission: "canEnableLiveScore", displayNames: ["Live Score"] },
    { permission: "canSendNotifications", displayNames: ["Notificaions"] },
    { permission: "canViewBettingPnL", displayNames: ["Betting P/L"] },
    { permission: "canViewPL", displayNames: ["Live PnL"] },
    { permission: "canViewAuditLogs", displayNames: ["Audit Log"] },
    { permission: "canManageRoles", displayNames: ["Roles"] },
    { permission: "canConfigureSystemConfigs", displayNames: ["Services Config"] },
    { permission: "canManageFeatureFlags", displayNames: ["Feature Flags"] },
    { permission: "canDoResettlement", displayNames: ["Settlement"] },
    { permission: "enableSupportChannels", displayNames: ["Customer Support"] },
  ];

  constructor(
    public navService: NavService,
    private readonly apiService: ApiService,
    public router: Router,
    media: MediaMatcher,
    changeDetectorRef: ChangeDetectorRef,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    this.mobileQuery = media.matchMedia("(min-width: 1100px)");
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addEventListener('change', this._mobileQueryListener);
    this.router.events.subscribe((event) => {
      this.parentActive = this.router.url.split("/")[1];
      if (event instanceof NavigationEnd) {
        this.validItems();
      }
    });
  }

  ngOnInit(): void {
    this.validItems();
  }

  validItems() {
    const udata = this.apiService.getUserData();
    const role = udata.ROLE;
    const parentRole = udata.PARENT_ROLE;
    this.isSuperAdmin = role == "super_admin";
    const permissions: string[] = udata.PERMISSIONS.split(",");
    const hasUserId = !!localStorage.getItem("userId");

    let items: NavItem[] = navItems.filter((item) => item.displayName == "Home");

    for (const { permission, displayNames } of this.PERMISSION_NAV_MAP) {
      if (permissions.includes(permission)) {
        items = [...items, ...navItems.filter((item) => displayNames.includes(item.displayName as string))];
      }
    }

    items = [...items, ...this.getConditionalItems(role, parentRole, permissions, hasUserId)];

    // Admin and super_admin only (the API routes allow only these roles too).
    if (role === "super_admin" || role === "admin") {
      items = [...items, ...navItems.filter((item) => item.displayName == "Bet Violations")];
    }

    if (permissions.includes("canHandleFunds")) {
      const fundNames = ["Deposit", "Withdraw", "Transfer", "Wallets"];
      items = [...items, ...navItems.filter((item) => item.navCap == "Funds" || fundNames.includes(item.displayName as string))];
    }

    items = [...items, ...navItems.filter((item) => item.displayName == "Change Password")];

    this.navItems = items;

    this.appendDomainSpecificItems(items);
    this.loadTickerItems(permissions);
  }

  private getConditionalItems(role: string, parentRole: string, permissions: string[], hasUserId: boolean): NavItem[] {
    const result: NavItem[] = [];
    const isAdminOrOperatorOfAdmin = this.isAdminOrOperatorOfAdmin(role, parentRole);

    const rules: { when: boolean; displayName: string }[] = [
      { when: permissions.includes("sportsNavigationManagement") || this.isSuperAdmin, displayName: "Sports Navigation" },
      { when: role !== "super_admin" && permissions.includes("canManagePermissions") && hasUserId, displayName: "Permissions" },
      { when: permissions.includes("canSetCommission") && hasUserId, displayName: "Static Commission" },
      { when: permissions.includes("canSetTakeRisk") && hasUserId, displayName: "Static Risk" },
      { when: permissions.includes("canViewBetInfo") && hasUserId, displayName: "Bet Info" },
      { when: permissions.includes("canConfigureBookmakerFancy") && permissions.includes("SB-BMAndFancy") && isAdminOrOperatorOfAdmin, displayName: "BM Fancy Nav Config" },
      { when: permissions.includes("canCreateOperator") && this.featureFlagService.getFeature("enableOperators"), displayName: "Operators" },
      { when: permissions.includes("setMaxMinBetSize") && isAdminOrOperatorOfAdmin, displayName: "Bet Config" },
      { when: this.isSuperAdmin || permissions.includes("canViewTransactions"), displayName: "Payments" },
      { when: permissions.includes("canViewRegistrationReport") && this.isAdminOrSuperMaster(role), displayName: "Registered Player Report" },
      { when: this.isSuperAdmin || (permissions.includes("canViewClientReport") && this.isAdminOrSuperMasterOrMaster(role)), displayName: "Client PNL Report" },
      { when: role === "master" && permissions.includes("canManagePaymentMethods"), displayName: "PSP Admin" },
      { when: this.isSuperAdmin, displayName: "VPN Whitelist" },
    ];

    for (const { when, displayName } of rules) {
      if (when) {
        result.push(...navItems.filter((item) => item.displayName === displayName));
      }
    }

    return result;
  }

  private appendDomainSpecificItems(items: NavItem[]): void {
    const oUserData = this.apiService.getUserToken();
    const domainName = oUserData == null ? "" : oUserData.domain_name;
    if (domainName === "kjsports9.com") {
      const kjAdminItem = navItems.find((item) => item.displayName === "KJsport9 Admin");
      if (kjAdminItem) {
        items.push(kjAdminItem);
      }
    }
  }

  private loadTickerItems(permissions: string[]): void {
    if (permissions.includes("canViewBetTicker")) {
      this.loadTickerFromConfig("BET_TICKER", "Bet Ticker");
    }
    if (permissions.includes("betTickerHybrid")) {
      this.loadTickerFromConfig("BET_TICKER_HYBRID", "Bet Ticker V1");
    }
  }

  private isAdminOrSuperMaster(role: string): boolean {
    return role === "admin" || role === "super_master";
  }

  private isAdminOrSuperMasterOrMaster(role: string): boolean {
    return role === "admin" || role === "super_master" || role === "master";
  }

  private isAdminOrOperatorOfAdmin(role: string, parentRole: string): boolean {
    return role === "admin" || (role === "operator" && parentRole === "admin");
  }
  private loadTickerFromConfig(configName: string, displayName: string) {
    const { DOMAIN_ID } = this.apiService.getUserData();

    const config$ =
      !DOMAIN_ID || DOMAIN_ID === "0"
        ? of<WhiteLabelMarketConfig[]>([])
        : this.apiService.send2ServerRelative(`white_label_market_config/${DOMAIN_ID}`, false).pipe(
            filter((res) => res.status === "ok"),
            map((res) => res.result),
          );

    config$
      .pipe(
        map((config) => {
          const isActive = (name: string): boolean => DOMAIN_ID === "0" || (config as any[]).some((c) => c.NAME === name && c.IS_ACTIVE);

          return isActive(configName) ? navItems.filter((i) => i.displayName === displayName) : [];
        }),
      )
      .subscribe((items) => {
        this.navItems = [...this.navItems, ...items];
      });
  }
}
